import { BarChartRounded, ExpandLess, ExpandMore, FilterListOffRounded } from "@mui/icons-material";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Collapse,
  Divider,
  Grid,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import { ITreeItem } from "src/models";
import { CoherenceGraph, CoherenceScore, CoherenceTeamPicker, LinearScaleResults, TextResponses } from ".";
import { HeaderLayout, IconLink, ISurveyInstancePickerOption, SurveyInstancePicker } from "../../../components";
import { configuration } from "../../../configuration";
import { Paths } from "../../../constants";
import { useBreakpoints, useSettings } from "../../../hooks";
import { useGetCoherenceScoreOverTime, useGetSurveyCoherenceReport, useGetTeamsTree } from "../../../http";
import { setTitlePrefix } from "../../../utilities";
import { getShortLabel } from "./utilities";

const CoherenceReportPage = () => {
  const theme = useTheme();
  setTitlePrefix(`Coherence Report`);

  const { isMdUp } = useBreakpoints();
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  const [selectedSurveyInstance, setSelectedSurveyInstance] = useState<ISurveyInstancePickerOption | undefined>(
    undefined
  );
  const [comparisonSurveyInstance, setComparisonSurveyInstance] = useState<ISurveyInstancePickerOption | undefined>(
    undefined
  );
  const [selectedTeamShortLabel, setSelectedTeamShortLabel] = useState<string>();
  const [comparisonTeamShortLabel, setComparisonTeamShortLabel] = useState<string>();

  const { coherenceReportSettings, updateCoherenceReportSettings } = useSettings();

  const { data: selectedTeams, isLoading: isLoadingSelectedTeams } = useGetTeamsTree(
    selectedSurveyInstance?.startDateTimeUtc,
    selectedSurveyInstance?.endDateTimeUtc
  );

  const { data: comparisonTeams, isLoading: isLoadingComparisonTeams } = useGetTeamsTree(
    comparisonSurveyInstance?.startDateTimeUtc,
    comparisonSurveyInstance?.endDateTimeUtc
  );

  const { data: report, isLoading: isLoadingReport } = useGetSurveyCoherenceReport(
    selectedSurveyInstance?.guid,
    coherenceReportSettings.selectedTeamGuids,
    comparisonSurveyInstance?.guid,
    coherenceReportSettings.comparisonTeamGuids
  );

  const { data: coherenceGraph, isLoading: isLoadingGraph } = useGetCoherenceScoreOverTime(
    coherenceReportSettings.selectedTeamGuids,
    coherenceReportSettings.comparisonTeamGuids
  );

  useEffect(() => {
    const teams = selectedTeams?.filter((x) => coherenceReportSettings.selectedTeamGuids?.includes(x.itemId)) ?? [];
    setSelectedTeamShortLabel(getShortLabel(teams) ?? "Me");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTeams]);

  useEffect(() => {
    const teams = comparisonTeams?.filter((x) => coherenceReportSettings.comparisonTeamGuids?.includes(x.itemId)) ?? [];
    setComparisonTeamShortLabel(getShortLabel(teams) ?? "-");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comparisonTeams]);

  function handleSelectedTeamChanged(teams: ITreeItem[]) {
    updateCoherenceReportSettings({
      selectedTeamGuids: teams.map((x) => x.itemId),
    });

    setSelectedTeamShortLabel(getShortLabel(teams) ?? "Me");
  }

  function handleComparisonTeamChanged(teams: ITreeItem[]) {
    updateCoherenceReportSettings({
      comparisonTeamGuids: teams.map((x) => x.itemId),
    });

    setComparisonTeamShortLabel(getShortLabel(teams) ?? "-");
  }

  function handleClearFiltersClicked() {
    setSelectedSurveyInstance(undefined);
    setComparisonSurveyInstance(undefined);

    updateCoherenceReportSettings({
      selectedTeamGuids: [],
      comparisonTeamGuids: undefined,
    });

    setSelectedTeamShortLabel("Me");
    setComparisonTeamShortLabel("-");
  }

  return (
    <HeaderLayout
      title={
        <Stack>
          <IconLink href={Paths.Reports} startIcon={<BarChartRounded fontSize="small" />}>
            Reports
          </IconLink>
          <Typography variant="h3">Coherence Report</Typography>
          <Typography>A multidimensional employee experience feedback loop report.</Typography>
        </Stack>
      }
      toolbar={
        <Stack width="100%">
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            display={{ xs: "flex", md: "none" }}
            mb={filtersExpanded ? 1 : 0}
          >
            <Button
              variant="tertiary"
              onClick={() => setFiltersExpanded(!filtersExpanded)}
              endIcon={filtersExpanded ? <ExpandLess /> : <ExpandMore />}
              color="inherit"
            >
              Filters
            </Button>
            <Button variant="tertiary" startIcon={<FilterListOffRounded />} onClick={handleClearFiltersClicked}>
              Clear
            </Button>
          </Stack>
          <Collapse in={isMdUp || filtersExpanded}>
            <Stack spacing={1} direction={{ xs: "column", lg: "row" }} justifyContent="space-between" width="100%">
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={{ xs: 0.5, sm: 1 }}
                divider={<Divider flexItem orientation="vertical" />}
              >
                <Stack spacing={0.5} direction={{ xs: "column", md: "row" }}>
                  <SurveyInstancePicker
                    surveyGuid={configuration.coherenceSurveyGuid}
                    surveyInstanceGuid={selectedSurveyInstance?.guid}
                    onChange={(value) => setSelectedSurveyInstance(value)}
                    hideActiveInstance
                  />
                  <CoherenceTeamPicker
                    isLoading={isLoadingSelectedTeams}
                    teams={selectedTeams ?? []}
                    selectedTeamGuids={coherenceReportSettings.selectedTeamGuids ?? []}
                    isMeVisible={true}
                    clearable
                    onTeamChanged={handleSelectedTeamChanged}
                    onTeamCleared={() => handleSelectedTeamChanged([])}
                  />
                </Stack>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  alignItems={{ xs: "inherit", md: "center" }}
                  spacing={{ xs: 0.5, sm: 1 }}
                >
                  <Typography>COMPARE TO</Typography>
                  <Stack spacing={0.5} direction={{ xs: "column", md: "row" }}>
                    <SurveyInstancePicker
                      surveyGuid={configuration.coherenceSurveyGuid}
                      surveyInstanceGuid={comparisonSurveyInstance?.guid}
                      onChange={(value) => setComparisonSurveyInstance(value)}
                      hideActiveInstance
                    />
                    <CoherenceTeamPicker
                      isLoading={isLoadingComparisonTeams}
                      teams={comparisonTeams ?? []}
                      selectedTeamGuids={coherenceReportSettings.comparisonTeamGuids ?? []}
                      isMeVisible={false}
                      clearable
                      onTeamChanged={handleComparisonTeamChanged}
                      onTeamCleared={() => handleComparisonTeamChanged([])}
                    />
                  </Stack>
                </Stack>
              </Stack>
              <Stack direction="row" spacing={1} display={{ xs: "none", lg: "flex" }}>
                <Button variant="tertiary" startIcon={<FilterListOffRounded />} onClick={handleClearFiltersClicked}>
                  Clear Filters
                </Button>
              </Stack>
            </Stack>
          </Collapse>
        </Stack>
      }
    >
      <Grid container spacing={{ xs: 0.5, sm: 1 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <CoherenceScore
                label="Selected"
                color={theme.palette.primary.main}
                teamLabel={selectedTeamShortLabel ?? "Me"}
                isLoading={isLoadingReport}
                coherenceSummary={report?.selectedTeamsCoherenceSummary}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <CoherenceScore
                label="Compare To"
                color={theme.palette.orange.main}
                teamLabel={comparisonTeamShortLabel ?? "-"}
                isLoading={isLoadingReport}
                coherenceSummary={report?.comparisonTeamsCoherenceSummary}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid size={12}>
          <CoherenceGraph
            isLoading={isLoadingGraph}
            data={coherenceGraph}
            selectedTeamLabel={selectedTeamShortLabel ?? "Me"}
            comparisonTeamLabel={comparisonTeamShortLabel ?? "-"}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card
            sx={{
              position: "sticky",
              top: "220px",
              maxHeight: `calc(100vh - 220px)`,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <CardHeader title={<Typography variant="h4">Rating Responses</Typography>} />
            <Divider sx={{ color: "#c9c9c9" }} />
            <LinearScaleResults
              isLoading={isLoadingReport}
              report={report}
              selectedLabel={`${selectedTeamShortLabel ?? "Me"}, ${selectedSurveyInstance?.name}`}
              comparisonLabel={`${comparisonTeamShortLabel ?? "-"}, ${comparisonSurveyInstance?.name}`}
            />
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card
            sx={{
              position: "sticky",
              top: "220px",
              maxHeight: `calc(100vh - 220px)`,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <CardHeader title={<Typography variant="h4">Text Responses</Typography>} />
            <Divider sx={{ color: "#c9c9c9" }} />
            <TextResponses isLoading={isLoadingReport} report={report} />
          </Card>
        </Grid>
      </Grid>
    </HeaderLayout>
  );
};

export { CoherenceReportPage };
